import { Fragment, useEffect, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { useController } from "react-hook-form";
import useOnclickOutside from "react-cool-onclickoutside";
import { InfoTooltip } from "../Tooltips/InfoTooltip";
import { getNestedValue } from "../../helpers";
import { getGeocode } from "use-places-autocomplete"; // JS-compatible

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function AddressAutocomplete({
  label,
  name,
  control,
  placeholder,
  helper,
  infoText,
  disabled,
  className,
  inputSize = "xl",
  onBlur,
  addOnRight, // <-- optional component on right
  countryRestriction = [], // array of country codes like ["CA"] or ["CA","US"]
  onSelectAddress, // callback receives mapped address object
}) {
  const {
    field: { onChange: formOnChange, value: formValue },
    formState: { errors },
  } = useController({ name, control });

  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [inputValue, setInputValue] = useState(formValue || "");
  const [predictions, setPredictions] = useState([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);

  const serviceRef = useRef(null);
  const timerRef = useRef(null);
  const ref = useOnclickOutside(() => setPredictions([]));

  const errorsForField = getNestedValue(errors, name);

  // Load Google Places API
  useEffect(() => {
    if (window.google?.maps?.places) {
      setGoogleLoaded(true);
      return;
    }
    const i = setInterval(() => {
      if (window.google?.maps?.places) {
        setGoogleLoaded(true);
        clearInterval(i);
      }
    }, 100);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (googleLoaded && !serviceRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, [googleLoaded]);

  useEffect(() => {
    setInputValue(formValue || "");
  }, [formValue]);

  // Fetch predictions
  const fetchPredictions = (value) => {
    if (!serviceRef.current || !value || value.trim().length < 2) {
      setPredictions([]);
      return;
    }

    setLoadingPredictions(true);

    const request = {
      input: value,
      types: ["address"],
      componentRestrictions:
        countryRestriction.length > 0
          ? { country: countryRestriction.map((c) => c.toLowerCase()) }
          : undefined,
    };

    serviceRef.current.getPlacePredictions(request, (result, status) => {
      setLoadingPredictions(false);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
        setPredictions(result);
      } else {
        setPredictions([]);
      }
    });
  };

  const onInputChange = (val) => {
    setInputValue(val);
    formOnChange(val);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchPredictions(val), 300);
  };

  // Handle selecting an address
  const handleSelect = async (description) => {
    const match = predictions.find((p) => p.description === description);
    const placeId = match?.place_id;

    setInputValue(description);
    formOnChange(description);
    setPredictions([]);

    if (!placeId) return;

    try {
      const results = await getGeocode({ placeId });
      if (!results || !results[0]) return;

      const components = results[0].address_components;

      const getComponent = (type) =>
        components.find((c) => c.types.includes(type))?.long_name || "";
      const getShort = (type) =>
        components.find((c) => c.types.includes(type))?.short_name || "";

      const mappedAddress = {
        address1:
          getComponent("street_number") && getComponent("route")
            ? `${getComponent("street_number")} ${getComponent("route")}`
            : description,
        address2: "", // optional
        city: getComponent("locality") || getComponent("sublocality") || "",
        state: {
          id: getShort("administrative_area_level_1"),
          name: getComponent("administrative_area_level_1"),
        },
        zip: getComponent("postal_code") || "",
        country: {
          id: getShort("country"),
          name: getComponent("country"),
        },
      };

      if (onSelectAddress) onSelectAddress(mappedAddress);
    } catch (err) {
      console.error("Failed to parse autocomplete address", err);
    }
  };

  const sizeClasses = {
    sm: "py-1 text-sm",
    md: "py-1.5 text-sm",
    lg: "py-2 text-base",
    xl: "py-3 text-base",
  };

  return (
    <div className={className}>
      <div className="flex justify-between">
        <label className="block text-sm font-light text-gray-900">
          <span>{label}</span>
          {infoText && <InfoTooltip text={infoText} className="ml-1" />}
        </label>
        {helper && <span className="text-xs text-gray-500">{helper}</span>}
      </div>

      <div className="mt-2 relative" ref={ref}>
        <Combobox value={inputValue} onChange={handleSelect}>
          <div className="relative">
            <Combobox.Input
              className={classNames(
                "block w-full rounded-md border-0 pr-16", // space for addon + icon
                sizeClasses[inputSize] || sizeClasses.md,
                errorsForField
                  ? "text-red-900 ring-red-300 placeholder:text-red-300"
                  : "placeholder:text-gray-400 ring-gray-200",
                "ring-1 ring-inset focus:ring-2 focus:ring-inset",
                disabled && "bg-gray-100 ring-transparent"
              )}
              onChange={(e) => onInputChange(e.target.value)}
              onBlur={onBlur}
              placeholder={placeholder}
              disabled={disabled || !googleLoaded}
            />

            {/* AddOn Right */}
            {addOnRight && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                {addOnRight}
              </div>
            )}

            {/* Dropdown icon */}
            <Combobox.Button className="absolute inset-y-0 right-14 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
            </Combobox.Button>

            {/* Suggestions dropdown */}
            <Transition as={Fragment}>
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg">
                {loadingPredictions && (
                  <div className="py-2 px-3 text-sm text-gray-500">Loading...</div>
                )}

                {!loadingPredictions && predictions.length === 0 && (
                  <div className="py-2 px-3 text-sm text-gray-400">No suggestions</div>
                )}

                {predictions.map((p) => (
                  <Combobox.Option
                    key={p.place_id}
                    value={p.description}
                    className={({ active }) =>
                      classNames(
                        "cursor-default select-none py-2 pl-3 pr-9",
                        active ? "bg-blue text-white" : "text-gray-900"
                      )
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames("block truncate", selected && "font-semibold")}
                        >
                          {p.description}
                        </span>
                        {selected && (
                          <span
                            className={classNames(
                              "absolute inset-y-0 right-0 flex items-center pr-4",
                              active ? "text-white" : "text-blue"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>

        {errorsForField && (
          <p className="mt-2 text-sm text-red-600">{errorsForField.message}</p>
        )}
      </div>
    </div>
  );
}